import { Injectable } from "@angular/core";
import { ApiService } from "./api.service";
import { Value } from "../models/value";
import { Rule } from "../models/rule";
import { MagicBoxRuleShort } from "../models/magicbox-rule-short";
import { MagicBoxRuleExtract } from "../models/magicboxRuleExtract";

@Injectable()
export class MagicBoxService {
  creationRuleFilters: any;

  /**
   * Load dependencies
   * @param {ApiService} apiService
   */
  constructor(private apiService: ApiService) {}

  /**
   * Fetch all referential types from the API
   * @param {boolean} isNonSportiveClassif
   * @returns {Observable<any>}
   */
  getReferentialTypes(isNonSportiveClassif: boolean) {
    return this.apiService
      .get("MagicBox/GetReferentialTypes/" + isNonSportiveClassif)
      .map((res) => {
        if (res && res.values) {
          return res.values.map((value) => new Value(value));
        }
      });
  }

  /**
   * Fetch all rules references from the API
   * @param {boolean} isNonSportiveClassif
   * @returns {Observable<any>}
   */
  getRulesReferences(isNonSportiveClassif: boolean) {
    return this.apiService
      .get("MagicBox/GetRulesReference/" + isNonSportiveClassif)
      .map((res) => {
        if (res && res.values) {
          return res.values.map((value) => new Value(value));
        }
      });
  }

  /**
   * Fetch all destinations from the API
   * @returns {Observable<any>}
   */
  getDestinations() {
    return this.apiService.get("MagicBox/GetDestinations").map((res) => {
      if (res && res.values) {
        return res.values.map((value) => new Value(value));
      }
    });
  }

  /**
   * Search magicbox rules with parameters with an API call
   * @param {number} languageId
   * @param {number} index
   * @param {number} limit
   * @param {boolean} orderByDescending
   * @param {number} orderOn
   * @param filters
   * @returns {Observable<any>}
   */
  search(
    languageId: number,
    index: number,
    limit: number,
    orderByDescending: boolean,
    orderOn: number,
    filters: any,
    defaultApplyTo: any
  ) {
    filters.languageId = languageId;

    if (filters.applyTo === null || filters.applyTo.length === 0) {
      filters.applyTo = defaultApplyTo;
    }

    const searchParams = {
      index: index,
      limit: limit,
      orderOn: orderOn,
      orderByDescending: orderByDescending,
      rule: filters,
      showDeleted: filters.showDeleted,
    };

    return this.apiService.post("MagicBox/Search", searchParams).map((res) => {
      if (res && res.values) {
        res.values = res.values.map((value) => new Rule(value));
      }
      return res;
    });
  }

  /**
   * Fetch a rule by id from the API
   * @param {number} ruleId
   * @returns {Observable<Rule>}
   */
  getById(ruleId: number, type: string) {
    return this.apiService
      .get("MagicBox/" + ruleId + "/" + (type === "4"))
      .map((res) => {
        if (res && res.value) {
          return new Rule(res.value);
        }
      });
  }

  /**
   * Delete a rule with an API call
   * @param {Rule} rule
   * @returns {Observable<any>}
   */
  deleteRule(rule: Rule) {
    return this.apiService.delete(
      "MagicBox/" + rule.id + "/" + (rule.for === 4)
    );
  }

  /**
   * Restor a rule
   * @param {Rule} rule
   * @returns {Observable<any>}
   */
  restoreRule(rule: Rule) {
    return this.apiService.get(
      "MagicBox/Restore/" + rule.id + "/" + (rule.for === 4)
    );
  }

  preDelete(rule: Rule) {
    rule.setDates();
    return this.apiService.post("MagicBox/PreDelete", rule).map((res) => {
      return res;
    });
  }

  deleteAll(rule: Rule) {
    return this.apiService
      .post("MagicBox/DeleteAll/" + (rule.for === 4), rule.linkedRules)
      .map((res) => {
        return res;
      });
  }

  deleteFieldsGenerated(magicBoxRuleShorts: MagicBoxRuleShort[]) {
    return this.apiService
      .post("MagicBox/DeleteGeneratedFields/", magicBoxRuleShorts)
      .map((res) => {
        return res;
      });
  }

  /**
   * Fetch all fields from the API
   * @returns {Observable<any>}
   */
  getFields() {
    return this.apiService.get("MagicBox/GetFields").map((res) => {
      if (res && res.values) {
        return res.values.map((value) => new Value(value));
      }
    });
  }

  /**
   * Update a rule with an API call
   * @param {Rule} rule
   * @returns {Observable<Rule>}
   */
  updateRule(rule: Rule) {
    rule.setDates();
    return this.apiService.post("MagicBox/PreUpdate", rule).map((res) => {
      return res;
    });
  }

  /**
   * Update a rule with an API call
   * @param {Rule} rule
   * @returns {Observable<Rule>}
   */
  updateAll(rule: Rule) {
    rule.setDates();
    return this.apiService.post("MagicBox/UpdateAll", rule).map((res) => {
      return res;
    });
  }

  /**
   * Create a rule with an API call
   * @param {Rule} rule
   * @returns {Observable<any>}
   */
  createRule(rule: Rule) {
    rule.setDates();
    return this.apiService.post("MagicBox/Create", rule).map((res) => {
      return res;
    });
  }

  /**
   * Fetch random referential entities according to rule parameters from the API
   * @param rule
   * @returns {Observable<any>}
   */
  getRandomReferentialEntities(rule: any) {
    return this.apiService.post("MagicBox/GetRandomReferentialEntities", rule);
  }

  /**
   * Get extract file fromt the API
   * @returns {Observable<any>}
   */
  getExtract(filters: any) {
    const criterias = {};
    Object.assign(criterias, filters);
    return this.apiService.post("MagicBox/Extract", criterias).map((res) => {
      return res.values.map((value) => new MagicBoxRuleExtract(value));
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/services/magic-box.service.ts